import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { routes } from './router'
import { initI18n } from './i18n'
import CartRehydrate from './store/CartRehydrate'
import { useCartStore } from './store/cartStore'
import { chunkReloader } from './appRuntime'
import { installAppUpdate } from './lib/appUpdate/install'
import './index.css'

// The browser router instance is created here (browser-only entry) rather than
// in router.tsx, so the prerender SSR entry can import `routes` without
// createBrowserHistory touching `document`.
const router = createBrowserRouter(routes)

// BUG-523: a tab open across a release picks it up. A chunk that no longer loads reloads the page
// once; a newer release (version.json) reloads it on the next page change, never mid-payment.
chunkReloader.listen(window)
if (import.meta.env.PROD) {
  installAppUpdate({
    router,
    buildSha: __BUILD_SHA__,
    win: window,
    doc: document,
    fetchImpl: window.fetch.bind(window),
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
})

const rootEl = document.getElementById('root')!
const app = (
  <StrictMode>
    {/* First child: its effect loads the saved basket before any page effect runs (#418). */}
    <CartRehydrate />
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
)

// The active locale's translation chunks must be in memory BEFORE the first
// render: prerendered pages ship real markup inside #root, and hydration must
// be byte-identical to it (see src/i18n.ts). initI18n() awaits exactly the
// active locale + the Danish fallback - the other locales stay lazy.
void initI18n().then(() => {
  // Prerendered pages ship real markup inside #root, so hydrate to reuse it.
  // Non-prerendered routes (the SPA fallback) ship an empty #root, so client-render.
  if (rootEl.hasChildNodes()) {
    hydrateRoot(rootEl, app)
  } else {
    // Nothing to match on a client-rendered route: load the basket before the first render.
    void useCartStore.persist.rehydrate()
    createRoot(rootEl).render(app)
  }
})
